import { useMemo } from 'react';

/**
 * Partition a list into named groups and sort each one, newest first by default.
 *
 * ## Why this exists
 *
 * Four phone screens need the same shape: split a list in two, sort each half by date
 * with the most recent at the top, and show a count on each filter pill. Fleet bookings
 * (Not completed / Completed), fleet invoices (Not paid / Paid), and now the workshop's
 * own bookings and invoices. Written inline that is four copies of the same reduce-and-
 * sort, and they drift — one sorts ascending, one forgets a null guard, one counts the
 * filtered list instead of the group.
 *
 * ## Design
 *
 * Headless on purpose. It returns data and renders nothing, so the four screens can look
 * however they need to while sharing the one thing that is genuinely identical. A
 * component would have forced them to share layout too, which is not the duplication
 * that exists.
 *
 * `keys` is required rather than inferred from the data. Inferring means an empty list
 * produces no groups at all, so the pills vanish exactly when the user most needs to see
 * "Paid 0" and realise they are looking at the wrong tab. Declaring the keys also makes
 * adding a third group a config change rather than an edit to this function.
 *
 * @param {Array}    items          the list, may be null or undefined
 * @param {object}   options
 * @param {string[]} options.keys   every group key, in display order
 * @param {Function} options.groupBy   (item) => key; a key not in `keys` is dropped
 * @param {Function} [options.sortBy]  (item) => comparable; omit to keep source order
 * @param {boolean}  [options.descending=true]  newest first
 * @returns {{ groups: Object<string, Array>, counts: Object<string, number> }}
 */
export default function useGroupedList(items, { keys, groupBy, sortBy, descending = true }) {
    return useMemo(() => {
        const groups = {};
        for (const key of keys) groups[key] = [];

        for (const item of items || []) {
            const key = groupBy(item);
            // An unknown key is dropped rather than silently creating a group nothing
            // renders — a row in a group with no pill is a row the user cannot reach.
            if (groups[key]) groups[key].push(item);
        }

        if (sortBy) {
            /*
             * Compared as strings when both sides are strings, which is what the dates
             * here are: ISO yyyy-MM-dd is fixed-width and zero-padded, so lexical order
             * is chronological order. No Date parsing, and no timezone to get wrong —
             * `new Date('2026-08-19')` is midnight UTC, which is the previous day in
             * Melbourne, and that has already cost this codebase a bug elsewhere.
             *
             * A missing value sorts last in both directions rather than throwing or
             * landing at the top: an undated row at the head of the list reads as the
             * newest one, which is the opposite of what is known about it.
             */
            const direction = descending ? -1 : 1;
            const compare = (a, b) => {
                const av = sortBy(a);
                const bv = sortBy(b);
                if (av == null && bv == null) return 0;
                if (av == null) return 1;
                if (bv == null) return -1;
                if (typeof av === 'string' && typeof bv === 'string') {
                    return av.localeCompare(bv) * direction;
                }
                if (av === bv) return 0;
                return (av < bv ? -1 : 1) * direction;
            };
            for (const key of keys) groups[key].sort(compare);
        }

        const counts = {};
        for (const key of keys) counts[key] = groups[key].length;

        return { groups, counts };
        // groupBy/sortBy are typically inline arrows and would change identity every
        // render, so the memo keys on the data and the group names instead. Both
        // callbacks must therefore be pure functions of their argument.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, keys.join('|'), descending]);
}
